import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Stack, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import DownloadIcon from '@mui/icons-material/Download';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';

/**
 * Visualizador de imagens com zoom, rotação e controles
 */
const ImageViewer = ({ 
  imageUrl, 
  fileName, 
  alt = 'Preview', 
  showControls = true 
}) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.25));
  };

  const resetZoom = () => {
    setScale(1);
    setRotation(0);
  };

  const rotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const rotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const handleDownload = () => {
    const anchor = document.createElement('a');
    anchor.href = imageUrl;
    anchor.download = fileName || 'imagem.jpg';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  if (imageError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          p: 3,
        }}
      >
        <Typography variant="body1" color="error">
          Erro ao carregar a imagem
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* Controles */}
      {showControls && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: { xs: 0.5, sm: 1 },
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Controles de zoom */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
            <Tooltip title="Diminuir zoom">
              <IconButton size="small" onClick={zoomOut}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Typography variant="caption" sx={{ minWidth: { xs: 50, sm: 60 }, textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              {Math.round(scale * 100)}%
            </Typography>
            
            <Tooltip title="Aumentar zoom">
              <IconButton size="small" onClick={zoomIn}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Resetar">
              <IconButton size="small" onClick={resetZoom}>
                <ZoomOutMapIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Controles de rotação */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
            <Tooltip title="Girar para esquerda">
              <IconButton size="small" onClick={rotateLeft}>
                <RotateLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Typography variant="caption" sx={{ minWidth: { xs: 35, sm: 40 }, textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              {rotation % 360}°
            </Typography>
            
            <Tooltip title="Girar para direita">
              <IconButton size="small" onClick={rotateRight}>
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Ação de download */}
          {fileName && (
            <Tooltip title="Baixar imagem">
              <IconButton size="small" onClick={handleDownload} color="primary">
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Imagem */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
          bgcolor: '#f5f5f5',
          position: 'relative',
        }}
      >
        {!imageLoaded && (
          <Typography variant="body2" color="text.secondary">
            Carregando imagem...
          </Typography>
        )}
        <img
          src={imageUrl}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          style={{
            maxWidth: imageLoaded ? '100%' : 0,
            maxHeight: imageLoaded ? '100%' : 0,
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-in-out',
            cursor: scale > 1 ? 'move' : 'default',
            objectFit: 'contain',
          }}
        />
      </Box>
    </Box>
  );
};

ImageViewer.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  alt: PropTypes.string,
  showControls: PropTypes.bool,
};

export default ImageViewer;
